import { useEffect, useRef, useState } from 'react'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
    getCheckoutCancelPath,
    getCheckoutFlow,
    getCheckoutSuccessPath,
    getValidatedCheckoutReturnTo,
} from '@/checkout-paths'
import { continueToStripeCheckout } from '@/components/checkout/page/controller'
import UnsupportedIosAppCheckout from '@/components/checkout/unsupported-ios-app-checkout'
import { useCreateStripeCheckoutSession } from '@/hooks/api/use-reservation-checkout'
import { isUnsupportedIosAppWrapper } from '@/stripe-checkout'

type CheckoutDirectStartViewProps = {
    onBack?: () => void
    isPwa?: boolean
}

export default function CheckoutDirectStartView({
    onBack,
    isPwa = false,
}: CheckoutDirectStartViewProps) {
    if (isUnsupportedIosAppWrapper(isPwa)) {
        return <UnsupportedIosAppCheckout onBack={onBack} />
    }

    return <SupportedCheckoutDirectStartView onBack={onBack} isPwa={isPwa} />
}

function SupportedCheckoutDirectStartView({
    onBack,
    isPwa = false,
}: CheckoutDirectStartViewProps) {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [checkoutError, setCheckoutError] = useState<string | null>(null)
    const checkoutFlow = getCheckoutFlow(location.search)
    const returnTo = getValidatedCheckoutReturnTo(location.search)
    const hasStartedDirectCheckoutRef = useRef(false)
    const setCheckoutRequestInProgress = (_inProgress: boolean) => {}
    const cancelPath = getCheckoutCancelPath({
        isPwa,
        locale: i18n.resolvedLanguage || i18n.language,
        checkoutFlow,
        returnTo,
    })

    const createStripeCheckoutSession = useCreateStripeCheckoutSession({
        setRequestInProgress: setCheckoutRequestInProgress,
        onErrorCallback: message => {
            setCheckoutError(message)
        },
        onSuccessCallback: () => {
            setCheckoutError(null)
        },
    })
    const stripeCheckoutSessionMutationRef = useRef(createStripeCheckoutSession)
    stripeCheckoutSessionMutationRef.current = createStripeCheckoutSession

    useEffect(() => {
        if (hasStartedDirectCheckoutRef.current) {
            return
        }

        hasStartedDirectCheckoutRef.current = true

        void continueToStripeCheckout({
            isPwa,
            createStripeCheckoutSession: input => stripeCheckoutSessionMutationRef.current.mutateAsync({
                ...input,
                checkoutFlow,
                returnTo,
            }),
            onCheckoutError: () => {
                setCheckoutError(t('checkout.paymentResult.genericError'))
            },
            buildNativePaymentSheetInput: ({ paymentId, paymentIntentClientSecret }) => ({
                paymentId,
                paymentIntentClientSecret,
                publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
                successUrl: getCheckoutSuccessPath({
                    isPwa,
                    locale: i18n.resolvedLanguage || i18n.language,
                    paymentId,
                    checkoutFlow,
                    returnTo,
                }),
                cancelUrl: cancelPath,
            }),
            onNativeCheckoutCompleted: paymentId => {
                navigate(getCheckoutSuccessPath({
                    isPwa,
                    locale: i18n.resolvedLanguage || i18n.language,
                    paymentId,
                    checkoutFlow,
                    returnTo,
                }), { replace: true })
            },
            onNativeCheckoutCanceled: () => {
                window.location.assign(cancelPath)
            },
            replaceLocation: url => {
                window.location.assign(url)
            },
        })
    }, [
        checkoutFlow,
        i18n.language,
        i18n.resolvedLanguage,
        isPwa,
        returnTo,
        cancelPath,
        navigate,
        t,
    ])

    if (checkoutError) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    flexDirection: 'column',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    py: 2,
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        backgroundColor: 'white',
                        borderRadius: '18px',
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        textAlign: 'center',
                    }}
                >
                    <ErrorOutlineRoundedIcon />
                    <Typography>{checkoutError}</Typography>
                    <Button
                        variant="solid"
                        onClick={onBack}
                        sx={{
                            fontSize: '15px',
                            padding: '0.875rem 1.25rem',
                            borderRadius: '7px',
                            backgroundColor: 'customBlack.main',
                        }}
                    >
                        {t('checkout.actions.back')}
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
            px: 2,
        }}>
            <Typography>{t('success.pending.title')}</Typography>
        </Box>
    )
}
